import { KB_BASE_URL } from '@/shared/config';

export type KbArticleRef = {
  slug: string;
  title: string;
};

export type KbSectionNode = {
  id: string;
  title: string;
  blurb?: string;
  sections: KbSectionNode[];
  articles: KbArticleRef[];
};

type KbStructureSectionDto = {
  section: {
    id: string;
    title: string;
    blurb?: string;
    parent?: string;
  };
  articles: Array<{ slug: string; title: string }>;
};

type KbStructureDto = {
  sections: KbStructureSectionDto[];
  // Pages without a KB section — the hub lists them at the nav root.
  rootArticles?: Array<{ slug: string; title: string }>;
};

export type KbStructure = {
  sections: KbSectionNode[];
  rootArticles: KbArticleRef[];
};

// One entry of the hub's /api/markets response: the full market label and
// the display languages its content supports.
export type KbMarketInfo = {
  code: string;
  label: string;
  defaultLanguage: string;
  languages: string[];
};

// Article IR contract — see content hub docs/API.md, KB section. @2 adds
// text styles, table blocks and inline media; @1 stays accepted during the
// hub's deploy window.
export const KB_IR_CONTRACT = 'content-kb-page@2';

const KB_IR_SUPPORTED_CONTRACTS: readonly string[] = [
  'content-kb-page@1',
  'content-kb-page@2',
];

export type KbIrTextMark = 'bold' | 'italic' | 'underline' | 'code';

// TextState styling carried on text inlines (contract @2).
export type KbIrTextStyles = { color?: string; background?: string };

export type KbIrTableCell = {
  header?: true;
  colSpan?: number;
  rowSpan?: number;
  children: KbIrInline[];
};

export type KbIrInline =
  | {
      type: 'text';
      text: string;
      marks?: KbIrTextMark[];
      styles?: KbIrTextStyles;
    }
  // A product button or field name, inline in a sentence. A label, never a
  // control: the renderer draws a pill and nothing is clickable.
  | { type: 'button'; label: string }
  // A step number written in the text: `(3)` is an open circle, `[3]` a
  // filled square. The hub lifts the marker out of the run, so the same
  // syntax works at the head of a heading and inside a sentence.
  | { type: 'badge'; number: number; shape: 'circle' | 'square' }
  | { type: 'link'; href: string; newTab?: boolean; children: KbIrInline[] }
  | { type: 'linebreak' }
  // Inline media (contract @2): the block media shapes minus the
  // block-only caption/variant fields, rendered as compact inline forms.
  // `width`/`height` are the stored file's pixel size when the hub knows
  // it; the renderer reserves the box from them while the bytes load.
  | {
      type: 'image';
      src: string;
      alt?: string;
      mime?: string;
      width?: number;
      height?: number;
    }
  | {
      type: 'video';
      src: string;
      poster?: string;
      mime?: string;
      filename?: string;
    }
  | { type: 'youtube'; videoId: string }
  | {
      type: 'document';
      downloadUrl: string;
      previewUrl?: string;
      mime?: string;
      filename?: string;
      title?: string;
      inlineable: boolean;
    };

export type KbIrNode =
  | { type: 'paragraph'; children: KbIrInline[] }
  | { type: 'quote'; children: KbIrInline[] }
  | {
      type: 'heading';
      level: 1 | 2 | 3 | 4;
      // Anchor id derived by the hub from the heading text, unique per
      // document. The hub's contents rail links to it.
      id?: string;
      children: KbIrInline[];
    }
  | { type: 'list'; ordered: boolean; items: KbIrInline[][] }
  | { type: 'table'; rows: KbIrTableCell[][] }
  | { type: 'horizontalrule' }
  | { type: 'code'; text: string; language?: string }
  | {
      type: 'image';
      src: string;
      alt?: string;
      caption?: string;
      mime?: string;
      width?: number;
      height?: number;
      variant?: 'block';
    }
  | {
      type: 'video';
      src: string;
      poster?: string;
      downloadUrl?: string;
      mime?: string;
      filename?: string;
      caption?: string;
      variant?: 'block';
    }
  | { type: 'youtube'; videoId: string; caption?: string }
  | {
      type: 'document';
      downloadUrl: string;
      previewUrl?: string;
      mime?: string;
      filename?: string;
      title?: string;
      label?: string;
      inlineable: boolean;
      variant?: 'block';
    }
  | {
      type: 'kb-link';
      entity: string;
      slug: string;
      title: string;
      href: string;
    };

export type KbIrArticle = {
  contract: 'content-kb-page@1' | 'content-kb-page@2';
  slug: string;
  market: string;
  lang: string;
  title: string;
  updated?: string | null;
  // Hero lead under the title; the hub's standalone page shows the same
  // text. Optional so an older hub without the field still renders.
  excerpt?: string | null;
  nodes: KbIrNode[];
};

// The hub serves a versioned Article IR envelope; consuming an unknown or
// missing contract string would silently mis-render the body.
export class KbContractError extends Error {
  readonly received: unknown;

  constructor(received: unknown) {
    super(
      `KB page returned unsupported contract: ${
        typeof received === 'string' ? received : 'missing'
      }`,
    );
    this.name = 'KbContractError';
    this.received = received;
  }
}

// The first page in navigation order: root articles sit above the section
// folds on the hub, then depth-first through the section tree.
export const firstKbArticle = (structure: KbStructure): KbArticleRef | null => {
  const firstInSection = (node: KbSectionNode): KbArticleRef | null =>
    node.articles[0] ??
    node.sections.map(firstInSection).find((found) => found !== null) ??
    null;

  return (
    structure.rootArticles[0] ??
    structure.sections.map(firstInSection).find((found) => found !== null) ??
    null
  );
};

export const nestKbSections = (
  sections: KbStructureSectionDto[],
): KbSectionNode[] => {
  const nodes = new Map<string, KbSectionNode>();
  const parentIdBySection = new Map<string, string>();
  const ordered: KbStructureSectionDto[] = [];

  sections.forEach(({ section, articles }) => {
    // First entry for an id wins; duplicates are dropped so a malformed
    // payload cannot double-place a node or overwrite a placed one.
    if (nodes.has(section.id)) return;

    nodes.set(section.id, {
      id: section.id,
      title: section.title,
      blurb: section.blurb,
      sections: [],
      articles: articles.map(({ slug, title }) => ({ slug, title })),
    });

    if (section.parent && section.parent !== section.id) {
      parentIdBySection.set(section.id, section.parent);
    }

    ordered.push({ section, articles });
  });

  // Nesting under a parent is only allowed when the parent's own chain
  // terminates: a cyclic parent reference would otherwise produce nodes
  // unreachable from the roots (or hang a naive renderer).
  const isAcyclicParent = (sectionId: string, parentId: string): boolean => {
    const visited = new Set<string>([sectionId]);
    let current = parentId;

    while (nodes.has(current)) {
      if (visited.has(current)) return false;

      visited.add(current);

      const next = parentIdBySection.get(current);

      if (next === undefined) return true;

      current = next;
    }

    return true;
  };

  const roots: KbSectionNode[] = [];

  ordered.forEach(({ section }) => {
    const node = nodes.get(section.id);

    if (!node) return;

    const parentId = parentIdBySection.get(section.id);
    const parent = parentId !== undefined ? nodes.get(parentId) : undefined;

    if (parent && parentId && isAcyclicParent(section.id, parentId)) {
      parent.sections.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
};

// The hub only sees an origin referrer from the legacy new-tab anchor,
// which was sent with no-referrer; keep that on the in-app requests too.
const fetchOptions: RequestInit = { referrerPolicy: 'no-referrer' };

// URL API version: the hub pins its response surface at /api/v1. The
// unversioned /api/* paths remain permanent aliases, so a 404 on the
// versioned path (hub not yet upgraded) falls back to the alias once.
const KB_API_VERSION = 'v1';

const versionedApiUrl = (path: string): string =>
  `${KB_BASE_URL}/api/${KB_API_VERSION}/${path}`;

const legacyApiUrl = (path: string): string => `${KB_BASE_URL}/api/${path}`;

// GET the versioned URL, falling back to the unversioned alias when the
// hub predates the v1 routes. Non-404 failures propagate untouched.
const fetchVersioned = async (path: string): Promise<Response> => {
  const response = await fetch(versionedApiUrl(path), fetchOptions);

  if (response.status === 404) {
    return fetch(legacyApiUrl(path), fetchOptions);
  }

  return response;
};

const structureQuery = (market: string, lang?: string): string => {
  const params = new URLSearchParams({ market });

  if (lang) params.set('lang', lang);

  return params.toString();
};

// The header keeps the Knowledge Base entry only while the content service
// answers: probe the static market directory and memoize the verdict. A
// success holds for the session; a failure is retried after a short window
// so a recovered service brings the entry back without a reload.
export const KB_AVAILABILITY_RETRY_MS = 30_000;

let availabilityVerdict: { value: boolean; at: number } | null = null;
let availabilityProbe: Promise<boolean> | null = null;

export const checkKbAvailability = (): Promise<boolean> => {
  if (!KB_BASE_URL) return Promise.resolve(false);

  const cached = availabilityVerdict;

  if (
    cached &&
    (cached.value || Date.now() - cached.at < KB_AVAILABILITY_RETRY_MS)
  ) {
    return Promise.resolve(cached.value);
  }

  availabilityProbe ??= fetchVersioned('markets')
    .then((response) => response.ok)
    .catch(() => false)
    .then((value) => {
      availabilityVerdict = { value, at: Date.now() };
      availabilityProbe = null;

      return value;
    });

  return availabilityProbe;
};

export const fetchKbStructure = async (
  market: string,
  lang?: string,
): Promise<KbStructure> => {
  const response = await fetchVersioned(
    `kb-structure?${structureQuery(market, lang)}`,
  );

  if (!response.ok) {
    throw new Error(`KB structure request failed: ${response.status}`);
  }

  const data: KbStructureDto = await response.json();

  return {
    sections: nestKbSections(data.sections ?? []),
    rootArticles: data.rootArticles ?? [],
  };
};

// The hub's static market directory: full labels + per-market content
// languages for the panel's picker.
export const fetchKbMarkets = async (): Promise<KbMarketInfo[]> => {
  const response = await fetchVersioned('markets');

  if (!response.ok) {
    throw new Error(`KB markets request failed: ${response.status}`);
  }

  const data: unknown = await response.json();

  return Array.isArray(data) ? (data as KbMarketInfo[]) : [];
};

const pageQuery = (market: string, slug: string, lang?: string): string => {
  const params = new URLSearchParams({ market, slug });

  if (lang) params.set('lang', lang);

  return params.toString();
};

export const fetchKbPage = async (
  market: string,
  slug: string,
  lang?: string,
): Promise<KbIrArticle> => {
  const response = await fetchVersioned(
    `kb-page?${pageQuery(market, slug, lang)}`,
  );

  if (!response.ok) {
    throw new Error(`KB page request failed: ${response.status}`);
  }

  // A null or non-object body has no contract to check; the optional
  // chain funnels it into the same error as a missing contract field.
  const data = (await response.json()) as Partial<KbIrArticle> | null;

  if (!data?.contract || !KB_IR_SUPPORTED_CONTRACTS.includes(data.contract)) {
    throw new KbContractError(data?.contract);
  }

  return data as KbIrArticle;
};
